import { PopoverPanel } from "@headlessui/react";
import { DateTime } from "luxon";
import { type FC, useState } from "react";
import { HiArrowRight, HiOutlineCheck, HiOutlineXMark } from "react-icons/hi2";
import { DatePicker } from "../primitives";
import { Menu } from "./Menu";

export type DateDisplayProps = {
	dueAt?: DateTime | null;
	completedAt?: DateTime | null;
	onDateDoubleClick?: () => void;
};

export type QuickEditDateDisplayProps = Omit<DateDisplayProps, "onDateDoubleClick"> & {
	onDateChange: (newDate: DateTime) => void;
};

export const DateDisplay: FC<DateDisplayProps> = ({ dueAt, completedAt, onDateDoubleClick }) => {
	if (!completedAt) {
		if (!dueAt) {
			return (
				<div data-testid="no-due-date" className="text-tertiary text-xs">
					No Due Date
				</div>
			);
		}

		return (
			<div
				data-testid="due-date-display"
				onDoubleClick={onDateDoubleClick}
				className="text-tertiary text-xs pt-[6px] h-6"
			>
				{dueAt.toLocal().toFormat("LLL dd, yyyy")}
			</div>
		);
	}

	return (
		dueAt && (
			<div data-testid="completed-date-display" className="flex gap-[10px] justify-start">
				<div className="text-gray-400 text-xs">{dueAt.toLocal().toFormat("LLL dd, yyyy")}</div>
				<HiArrowRight className="text-gray-400 w-[7.2px]" />
				<div className="text-gray-800 text-xs">
					{completedAt.toLocal().toFormat("LLL dd, yyyy")}
				</div>
			</div>
		)
	);
};

export const QuickEditDateDisplay: FC<QuickEditDateDisplayProps> = ({
	dueAt,
	completedAt,
	onDateChange,
}) => {
	const [showPicker, setShowPicker] = useState(false);
	const [inputValue, setInputValue] = useState(dueAt);

	return showPicker && dueAt ? (
		<div className="flex items-center gap-1">
			<div className="text-xs text-tertiary pt-[4px]">Due: </div>
			<Menu
				buttonClassName="text-xs pt-[2px]"
				button={
					<div className="text-tertiary font-[450] underline cursor-pointer hover:text-gray-800">
						{inputValue?.toFormat("LLL dd, yyyy")}
					</div>
				}
				anchor="top"
			>
				<PopoverPanel>
					{({ close }) => (
						<DatePicker
							value={inputValue ? inputValue.toJSDate() : undefined}
							defaultMonth={DateTime.now().toJSDate()}
							onSelect={(day) => {
								if (day) setInputValue(DateTime.fromJSDate(day));
								close();
							}}
						/>
					)}
				</PopoverPanel>
			</Menu>
			<div className="flex items-center gap-1 pt-0.5">
				<button
					title="Confirm"
					data-testid="quick-edit-date-confirm"
					className="cursor-pointer"
					onClick={(e) => {
						e.stopPropagation();
						if (inputValue) {
							onDateChange(inputValue);
						}
						setShowPicker(false);
					}}
				>
					<HiOutlineCheck color="green" size={16} />
				</button>
				<button
					title="Cancel"
					data-testid="quick-edit-date-cancel"
					className="cursor-pointer"
					onClick={(e) => {
						e.stopPropagation();
						setInputValue(dueAt);
						setShowPicker(false);
					}}
				>
					<HiOutlineXMark color="red" size={16} />
				</button>
			</div>
		</div>
	) : (
		<DateDisplay
			dueAt={dueAt}
			completedAt={completedAt}
			onDateDoubleClick={() => setShowPicker(true)}
		/>
	);
};
